import * as PIXI from 'pixi.js';
import { AudioSystem } from '../assets/AudioSystem';

interface AboutPageOptions {
  onClose?: () => void;
  audioSystem?: AudioSystem | null;
}

export class AboutPage {
  private container: PIXI.Container;
  private visible: boolean = false;
  private scrollContainer: PIXI.Container;
  private scrollY: number = 0;
  private maxScroll: number = 0;
  private options: AboutPageOptions;

  constructor(options: AboutPageOptions = {}) {
    this.options = options;
    this.container = new PIXI.Container();
    this.scrollContainer = new PIXI.Container();
    this.setupPage();
    this.container.visible = false;
  }

  private setupPage(): void {
    const screenWidth = 1920;
    const screenHeight = 1080;

    // Backdrop
    const backdrop = new PIXI.Graphics();
    backdrop.beginFill(0x000000, 0.95);
    backdrop.drawRect(0, 0, screenWidth, screenHeight);
    backdrop.endFill();
    backdrop.eventMode = 'static';
    backdrop.cursor = 'pointer';
    // Right-click to close
    backdrop.on('rightdown', () => {
      this.options.audioSystem?.playSound('button_click');
      this.hide();
    });
    this.container.addChild(backdrop);

    // Panel with gradient effect
    const panelWidth = 1200;
    const panelHeight = 800;
    const panelX = (screenWidth - panelWidth) / 2;
    const panelY = (screenHeight - panelHeight) / 2;

    const panel = new PIXI.Graphics();
    panel.beginFill(0x1a1a1a, 0.95);
    panel.drawRoundedRect(0, 0, panelWidth, panelHeight, 12);
    panel.endFill();
    panel.lineStyle(4, 0x666666, 0.9);
    panel.drawRoundedRect(0, 0, panelWidth, panelHeight, 12);
    panel.x = panelX;
    panel.y = panelY;
    panel.eventMode = 'static';
    this.container.addChild(panel);

    // Decorative border
    const border = new PIXI.Graphics();
    border.lineStyle(2, 0xffd700, 0.6);
    border.drawRoundedRect(panelX + 5, panelY + 5, panelWidth - 10, panelHeight - 10, 10);
    this.container.addChild(border);

    // Scrollable area with mask
    const scrollMask = new PIXI.Graphics();
    scrollMask.beginFill(0x000000);
    scrollMask.drawRect(panelX + 30, panelY + 100, panelWidth - 60, panelHeight - 150);
    scrollMask.endFill();
    this.container.addChild(scrollMask);

    this.scrollContainer.x = panelX + 30;
    this.scrollContainer.y = panelY + 100;
    this.scrollContainer.mask = scrollMask;
    this.container.addChild(this.scrollContainer);

    let currentY = 0;
    const contentWidth = panelWidth - 80;
    const contentX = 10;

    // Title with glow effect
    const title = new PIXI.Text('MAZING HUNTERS', {
      fontFamily: 'Courier New',
      fontSize: 56,
      fill: 0xffd700,
      fontWeight: 'bold',
      stroke: 0x000000,
      strokeThickness: 4,
    });
    title.x = contentX + (contentWidth - title.width) / 2;
    title.y = currentY;
    this.scrollContainer.addChild(title);
    currentY += 80;

    // Subtitle
    const subtitle = new PIXI.Text('A Strategic Tower Defense Experience', {
      fontFamily: 'Courier New',
      fontSize: 28,
      fill: 0xcccccc,
      fontStyle: 'italic',
    });
    subtitle.x = contentX + (contentWidth - subtitle.width) / 2;
    subtitle.y = currentY;
    this.scrollContainer.addChild(subtitle);
    currentY += 60;

    // Genre section
    const genreTitle = new PIXI.Text('Genre', {
      fontFamily: 'Courier New',
      fontSize: 36,
      fill: 0xffe082,
      fontWeight: 'bold',
    });
    genreTitle.x = contentX;
    genreTitle.y = currentY;
    this.scrollContainer.addChild(genreTitle);
    currentY += 50;

    const genreText = new PIXI.Text(
      'Strategic Tower Defense\n' +
      'Sub-genre: Maze Builder / Path Control\n\n' +
      'Mazing Hunters combines classic tower defense mechanics with strategic maze building. ' +
      'Unlike traditional TD games where enemies follow fixed paths, here you control the battlefield ' +
      'by placing walls to create your own maze. Design optimal kill zones, funnel enemies through chokepoints, ' +
      'and optimize your tower placement for maximum effectiveness.',
      {
        fontFamily: 'Courier New',
        fontSize: 24,
        fill: 0xffffff,
        wordWrap: true,
        wordWrapWidth: contentWidth,
        lineHeight: 32,
        align: 'left',
      }
    );
    genreText.x = contentX;
    genreText.y = currentY;
    this.scrollContainer.addChild(genreText);
    currentY += genreText.height + 40;

    // Core Mechanics
    const mechanicsTitle = new PIXI.Text('Core Mechanics', {
      fontFamily: 'Courier New',
      fontSize: 36,
      fill: 0xffe082,
      fontWeight: 'bold',
    });
    mechanicsTitle.x = contentX;
    mechanicsTitle.y = currentY;
    this.scrollContainer.addChild(mechanicsTitle);
    currentY += 50;

    const mechanicsText = new PIXI.Text(
      '• Build & Upgrade: Place towers and walls strategically, upgrade towers through three tiers (A, B, C)\n\n' +
      '• Maze Creation: Design custom paths using walls to control enemy movement\n\n' +
      '• Block Breaker: Enemies will break through walls if completely blocked - plan your maze carefully!\n\n' +
      '• Economy Management: Limited gold income creates meaningful strategic decisions\n\n' +
      '• Progressive Difficulty: Enemies scale exponentially while your economy has hard limits\n\n' +
      '• Wave System: Manual or automatic wave progression with boss encounters',
      {
        fontFamily: 'Courier New',
        fontSize: 24,
        fill: 0xffffff,
        wordWrap: true,
        wordWrapWidth: contentWidth,
        lineHeight: 32,
        align: 'left',
      }
    );
    mechanicsText.x = contentX;
    mechanicsText.y = currentY;
    this.scrollContainer.addChild(mechanicsText);
    currentY += mechanicsText.height + 40;

    // Design Philosophy
    const designTitle = new PIXI.Text('Design Philosophy', {
      fontFamily: 'Courier New',
      fontSize: 36,
      fill: 0xffe082,
      fontWeight: 'bold',
    });
    designTitle.x = contentX;
    designTitle.y = currentY;
    this.scrollContainer.addChild(designTitle);
    currentY += 50;

    const designText = new PIXI.Text(
      'Mazing Hunters is designed to be challenging and strategic. The game is balanced against the player - ' +
      'enemies become progressively stronger while your economy has hard limits. This creates a game where ' +
      'optimization and strategic planning are essential for survival.\n\n' +
      'The game intentionally becomes unwinnable at higher waves, creating a "how long can you survive?" ' +
      'challenge rather than an endless progression. Every decision matters, from early game tower placement ' +
      'to late game upgrade priorities.',
      {
        fontFamily: 'Courier New',
        fontSize: 24,
        fill: 0xffffff,
        wordWrap: true,
        wordWrapWidth: contentWidth,
        lineHeight: 32,
        align: 'left',
      }
    );
    designText.x = contentX;
    designText.y = currentY;
    this.scrollContainer.addChild(designText);
    currentY += designText.height + 30;

    this.maxScroll = Math.max(0, currentY - (panelHeight - 150));

    // Scroll buttons
    const scrollUpBtn = new PIXI.Graphics();
    scrollUpBtn.beginFill(0x444444, 0.9);
    scrollUpBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollUpBtn.endFill();
    scrollUpBtn.lineStyle(2, 0x888888);
    scrollUpBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollUpBtn.x = panelX + (panelWidth - 200) / 2;
    scrollUpBtn.y = panelY + 50;
    scrollUpBtn.eventMode = 'static';
    scrollUpBtn.cursor = 'pointer';
    scrollUpBtn.on('pointerdown', () => {
      this.options.audioSystem?.playSound('button_click');
      this.scrollY = Math.max(0, this.scrollY - 100);
      this.updateScroll();
    });
    this.container.addChild(scrollUpBtn);

    const scrollUpText = new PIXI.Text('▲ Scroll Up', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
      align: 'center',
    });
    scrollUpText.x = scrollUpBtn.x + 100 - scrollUpText.width / 2;
    scrollUpText.y = scrollUpBtn.y + 20 - scrollUpText.height / 2;
    this.container.addChild(scrollUpText);

    const scrollDownBtn = new PIXI.Graphics();
    scrollDownBtn.beginFill(0x444444, 0.9);
    scrollDownBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollDownBtn.endFill();
    scrollDownBtn.lineStyle(2, 0x888888);
    scrollDownBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollDownBtn.x = panelX + (panelWidth - 200) / 2;
    scrollDownBtn.y = panelY + panelHeight - 50;
    scrollDownBtn.eventMode = 'static';
    scrollDownBtn.cursor = 'pointer';
    scrollDownBtn.on('pointerdown', () => {
      this.options.audioSystem?.playSound('button_click');
      this.scrollY = Math.min(this.maxScroll, this.scrollY + 100);
      this.updateScroll();
    });
    this.container.addChild(scrollDownBtn);

    const scrollDownText = new PIXI.Text('▼ Scroll Down', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
      align: 'center',
    });
    scrollDownText.x = scrollDownBtn.x + 100 - scrollDownText.width / 2;
    scrollDownText.y = scrollDownBtn.y + 20 - scrollDownText.height / 2;
    this.container.addChild(scrollDownText);

    // Close button
    const closeButton = new PIXI.Graphics();
    closeButton.beginFill(0x006600, 0.9);
    closeButton.drawRoundedRect(0, 0, 250, 50, 8);
    closeButton.endFill();
    closeButton.lineStyle(2, 0x00aa00);
    closeButton.drawRoundedRect(0, 0, 250, 50, 8);
    closeButton.x = panelX + panelWidth - 280;
    closeButton.y = panelY + panelHeight - 70;
    closeButton.eventMode = 'static';
    closeButton.cursor = 'pointer';
    closeButton.on('pointerdown', () => {
      this.options.audioSystem?.playSound('button_click');
      this.hide();
    });
    this.container.addChild(closeButton);

    const closeText = new PIXI.Text('Close', {
      fontFamily: 'Courier New',
      fontSize: 24,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    closeText.x = closeButton.x + closeButton.width / 2 - closeText.width / 2;
    closeText.y = closeButton.y + closeButton.height / 2 - closeText.height / 2;
    this.container.addChild(closeText);
  }

  show(): void {
    this.visible = true;
    this.container.visible = true;
    this.scrollY = 0;
    this.updateScroll();
    // Enable pointer events on the info pages canvas
    if ((window as any).infoPagesCanvas) {
      (window as any).infoPagesCanvas.style.pointerEvents = 'auto';
    }
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
    // Disable pointer events when hidden
    if ((window as any).infoPagesCanvas) {
      (window as any).infoPagesCanvas.style.pointerEvents = 'none';
    }
    this.options.onClose?.();
  }

  private updateScroll(): void {
    const screenHeight = 1080;
    const panelY = (screenHeight - 800) / 2;
    this.scrollContainer.y = panelY + 100 - this.scrollY;
  }

  getContainer(): PIXI.Container {
    return this.container;
  }
}

